import Script from 'next/script';

/**
 * DilaCRM Website Chat Widget (Salesmate `web_widget` channel).
 *
 * Loads the embeddable chat bubble/panel from a DilaCRM instance
 * (crm.dilaweb.com). The script is a self-contained, Shadow-DOM isolated IIFE
 * (see @salesmate/widget) — it renders nothing until it has mounted, fails
 * silently on any error, and never touches this page's DOM outside its own
 * shadow root.
 *
 * The widget key identifies the *workspace conversations land in*. The key
 * that shipped with the DataCode-era site would route this corporation's
 * visitors into that workspace's inbox, which is the same silent-misdelivery
 * problem the contact forms had (see src/lib/forms.ts). So the widget is now
 * opt-in: it renders only when this deployment sets its own key in
 * `NEXT_PUBLIC_DILACODE_WIDGET_KEY`. Unset — as it is by default — nothing
 * loads and no request is made.
 *
 * `afterInteractive` (Next's default background-load strategy) mirrors how
 * Plausible is loaded elsewhere in this layout — the widget isn't needed for
 * first paint, so it shouldn't compete with the LCP path.
 */
export default function WebWidgetScript() {
  const widgetKey = process.env.NEXT_PUBLIC_DILACODE_WIDGET_KEY?.trim();
  if (!widgetKey) return null;

  const src = process.env.NEXT_PUBLIC_DILACODE_WIDGET_SRC?.trim() || 'https://crm.dilaweb.com/widget.js';

  return (
    <Script
      id="dilacrm-web-widget"
      strategy="afterInteractive"
      src={src}
      data-salesmate-widget={widgetKey}
    />
  );
}
